import React from 'react'
import { graphql } from 'react-apollo'
import { withRouter } from 'react-router-dom'

import { useAccountState } from '@acorns/web-bank-linking'
import {
  branch,
  compose,
  fromRenderProps,
  renderComponent,
  withHandlers,
  withProps,
} from 'recompose'

import CheckIconImage from 'assets/icons/check-green.svg'
import BankImage from 'assets/images/bank-link-core-black.svg'
import BankLinkSuccessAccordion from 'assets/images/bank-link-success-accordion.svg'
import SuccessImage from 'assets/images/bank-link-success.png'
import Button from 'components/Button'
import CenteredSpinner from 'components/CenteredSpinner'
import { CTAContainer } from 'components/CTAContainer'
import withHeroText, { HeroTextProps } from 'components/HOCs/withHeroText'
import withRemoteStrings, { RemoteStringsProps } from 'components/HOCs/withRemoteStrings'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { ActionType, BankCardOrderStatus, PageName } from 'generated/types'
import fundingInitiatedQuery from 'pages/Funding/Checking/gql/funding-initiated-query.gql'
import {
  FundingInitiatedQuery,
  shouldTreatAsSoftFail,
} from 'pages/Funding/Checking/useFundingInitiatedPolling'
import { TestIDs } from 'test/utils/testIds'
import trackEvent from 'utils/analytics/trackEvent'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext, FunnelContextProps } from 'utils/FunnelContext'

import { resolveSuccessButtonTappedEvent } from '../Institutions/DirectIntegration/events'
import roundUpsQuery from './roundUps.query.gql'
import {
  AccountContainer,
  AccountDetails,
  AccountName,
  BankImageContainer,
  CheckIcon,
  Container,
  Image,
  LaterImage,
  LaterText,
  Text,
  Title,
} from './styled-components'

type Props = {
  header: string
  text: string
  title: string
  handleClick: () => void
  fundingSourceLastFour?: string
  fundingSourceName?: string
  financialInstitutionLogoUrl?: string
  financialInstitutionName?: string
} & WithFlowRouterProps &
  HeroTextProps &
  RemoteStringsProps &
  FunnelContextProps

const NextButton = withClickTrack<any, any>(TRACKER_IDS.BANK_LINK_SUCCESS.NEXT_BUTTON)(Button)

export const enhance = compose<Props, {}>(
  fromRenderProps<Partial<FunnelContextProps>, {}, Partial<FunnelContextProps>>(
    FunnelContext.Consumer,
    ({ isBackEnabled, hasVisited, isDynamicFunnelEnabled, routeToPage }) => ({
      isBackEnabled,
      hasVisited,
      isDynamicFunnelEnabled,
      routeToPage,
    }),
  ),
  withRouter,
  withFlowRouter,
  withHeroText,
  withRemoteStrings,
  graphql<{}, FundingInitiatedQuery, {}, { isSoftFail: boolean; loading: boolean }>(
    fundingInitiatedQuery,
    {
      props: ({ data }) => {
        const { spendAccount, linkedAccounts } = data
        return {
          loading: data?.loading,
          didOrderCard:
            spendAccount?.bankCardOrder.cardOrderStatus !== BankCardOrderStatus.NotOrdered,
          isSoftFail: shouldTreatAsSoftFail({
            spendAccount,
            linkedAccounts,
          }),
        }
      },
    },
  ),
  graphql(roundUpsQuery, { options: { fetchPolicy: 'network-only' } }),
  withProps(({ getRemoteString, heroText, location, data: { loading, roundUpsProfile } }) => {
    if (loading) {
      return {
        loading,
      }
    } else {
      return {
        financialInstitutionName: location?.state?.financialInstitution?.name,
        financialInstitutionLogoUrl: location?.state?.financialInstitution?.logoUrl,
        fundingSourceLastFour: location?.state?.linkedSubaccounts?.[0]?.accountNumberLastFour,
        fundingSourceName: location?.state?.linkedSubaccounts?.[0]?.name,
        isManualFundingSource: location?.state?.isManualFundingSource,
        roundUpsEnabled: roundUpsProfile && roundUpsProfile.roundUpsEnabled,
        header: getRemoteString('bank_link_success_header', heroText.header),
        text: getRemoteString('bank_link_success_text', heroText.text),
        title: heroText.title,
      }
    }
  }),
  withHandlers({
    handleClick: ({
      route,
      roundUpsEnabled,
      isSoftFail,
      didOrderCard,
      isManualFundingSource,
      isDynamicFunnelEnabled,
      routeToPage,
      hasVisited,
    }) => () => {
      const shouldSkip = didOrderCard && (isSoftFail || isManualFundingSource)

      if (isDynamicFunnelEnabled) {
        if (hasVisited(PageName.SecondChanceBankLink) || hasVisited(PageName.RecurringInvestment)) {
          roundUpsEnabled
            ? routeToPage(PageName.BankLinkSuccess, ActionType.Switch)
            : routeToPage(PageName.BankLinkSuccess, ActionType.Skip)
        } else if (hasVisited(PageName.OneTimeInvestment)) {
          roundUpsEnabled
            ? routeToPage(PageName.BankLinkSuccess, ActionType.Switch)
            : routeToPage(PageName.BankLinkSuccess, ActionType.Direct)
        } else {
          routeToPage(PageName.BankLinkSuccess, shouldSkip ? ActionType.Skip : ActionType.Continue)
        }
      } else {
        const action = shouldSkip ? 'skip' : 'continue'
        route({ from: FlowRoute.BankLinkSuccess, action, state: { roundUpsEnabled } })
      }
    },
  }),
  branch(({ loading }) => loading, renderComponent(CenteredSpinner)),
)

export const BankLinkSuccess: React.FC<Props> = ({
  handleClick,
  financialInstitutionName,
  financialInstitutionLogoUrl,
  flow,
  fundingSourceLastFour,
  header,
  text,
  title,
  isDynamicFunnelEnabled,
  isBackEnabled,
}) => {
  const { financialInstitution } = useAccountState()

  const handleNextClick = () => {
    if (financialInstitution?.linkProviderType === 'DIRECT_BANK') {
      trackEvent('Button Tapped', {
        eventData: resolveSuccessButtonTappedEvent(financialInstitution.name),
        flow,
      })
    }

    handleClick()
  }

  return (
    <>
      <Nav back={isDynamicFunnelEnabled && isBackEnabled(PageName.BankLinkSuccess)} />
      <Layout>
        <Container>
          {flow === 'later' ? (
            <>
              <LaterImage src={SuccessImage} />
              <Title>{header}</Title>
              <LaterText>{text}</LaterText>
            </>
          ) : (
            <>
              <Image src={BankLinkSuccessAccordion} />
              <Title>{title}</Title>
              <Text>{text}</Text>
              <AccountContainer>
                <BankImageContainer src={financialInstitutionLogoUrl ?? BankImage} />
                <div>
                  {financialInstitutionName && (
                    <AccountName>{financialInstitutionName}</AccountName>
                  )}
                  {fundingSourceLastFour && (
                    <AccountDetails>Ending in {fundingSourceLastFour}</AccountDetails>
                  )}
                </div>
                <CheckIcon src={CheckIconImage} />
              </AccountContainer>
            </>
          )}
          <Separator />
          <CTAContainer>
            <NextButton
              data-testid={TestIDs.BANK_LINK_SUCCESS.SUBMIT_BUTTON}
              onPress={handleNextClick}
            >
              Next
            </NextButton>
          </CTAContainer>
        </Container>
      </Layout>
    </>
  )
}

export default enhance(BankLinkSuccess)
